import { useEffect, useRef } from "react";
import { pushLayer, popLayer, isTopLayer } from "./modalLayers";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), ' +
  '[tabindex]:not([tabindex="-1"])';

/**
 * useFocusTrap
 *
 * Prende o foco dentro de `containerRef` enquanto `isActive` for true.
 * Chama `onEscape` quando a tecla Escape for pressionada.
 *
 * Uso: passar um ref para o elemento container do modal/dialog.
 *
 * CAMADAS: enquanto ativo, registra-se em `modalLayers` e so responde a
 * Escape/Tab se for a camada do TOPO. Sem isso, um sub-modal aberto sobre
 * outro modal deixava os dois traps escutando o mesmo `keydown` no document —
 * Escape fechava o modal EXTERNO e o sub-formulario ia junto (ver
 * hooks/modalLayers.js). Com uma unica camada aberta, o comportamento e
 * exatamente o de antes.
 */
export function useFocusTrap(containerRef, isActive, onEscape) {
  // Ref para onEscape evita que o effect re-execute (e re-foque) a cada
  // re-render do pai — que acontece a cada keystroke quando o pai tem estado.
  const onEscapeRef = useRef(onEscape);
  useEffect(() => { onEscapeRef.current = onEscape; });

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    // Identidade unica desta camada — objeto vazio serve, so precisa ser !==
    // de qualquer outro. Recriado a cada ativacao, de proposito.
    const layer = {};
    pushLayer(layer);

    const getFocusable = () =>
      [...container.querySelectorAll(FOCUSABLE)].filter(
        (el) => !el.closest('[inert]') && getComputedStyle(el).display !== 'none'
      );

    // Foca o primeiro elemento interativo ao abrir
    const initial = getFocusable()[0];
    initial?.focus();

    const handleKeyDown = (e) => {
      // Camada de baixo nao reage enquanto houver algo por cima.
      if (!isTopLayer(layer)) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onEscapeRef.current?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); return; }

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !container.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      popLayer(layer);
    };
  }, [containerRef, isActive]); // onEscape fora das deps — lido via ref acima
}
